// ⚙️ Configuración de Emisión Electrónica — Financiero / Configuración
// Pantalla país-aware: para empresas EC (SRI) gestiona ambiente/numeración/régimen;
// para empresas PE (SUNAT) gestiona ambiente, Clave SOL, certificado .p12 y series.
// El .p12 se coloca en el servidor (aquí se registran su ruta y contraseña).
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileSignature, Loader2, Save, ShieldCheck, ShieldAlert, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient } from '@/lib/api/client';
import { EmisionAutomatica } from './EmisionAutomatica';
import { EstablecimientosPuntos, ptoListar, type Establecimiento, type Punto } from './EstablecimientosPuntos';
import { ConfiguracionXML } from './ConfiguracionXML';

const FIN = { headers: { 'Content-Profile': 'financiero', 'Accept-Profile': 'financiero' } };
const inputCls = 'h-10 w-full rounded-md border border-input bg-background px-3 text-sm';
const selectCls = 'h-10 rounded-md border border-input bg-background px-3 text-sm';

interface Config {
  pais: string;
  configurado?: boolean;
  ambiente: string; tipo_emision: string; establecimiento: string; punto_emision: string;
  secuencial_actual: number; obligado_contabilidad: boolean;
  contribuyente_especial: string | null; rimpe: string | null; agente_retencion: string | null;
  dir_matriz: string | null; dir_establecimiento: string | null; cert_configurado: boolean;
  // PE (SUNAT)
  sol_usuario: string | null; serie_factura: string | null; serie_boleta: string | null; serie_nc: string | null;
  cert_path: string | null; p12_configurado: boolean; sol_configurado: boolean;
}

export function ConfiguracionEmision() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery<Config>({
    queryKey: ['feEmisionConfig'],
    queryFn: async () => (await apiClient.post('/rpc/fe_emision_config_get', {}, FIN)).data,
  });
  const { data: establecimientos = [] } = useQuery<Establecimiento[]>({
    queryKey: ['establecimientos'],
    queryFn: async () => (await apiClient.post('/rpc/est_listar', {}, FIN)).data,
  });
  const { data: puntos = [] } = useQuery<Punto[]>({ queryKey: ['puntos-emision'], queryFn: ptoListar });

  const [tab, setTab] = useState<'emision' | 'xml'>('emision');
  const [f, setF] = useState<Config | null>(null);
  const [solPwd, setSolPwd] = useState('');   // write-only: en blanco = no cambiar
  const [p12Pwd, setP12Pwd] = useState('');
  useEffect(() => { if (data) setF(data); }, [data]);

  const invalidar = () => { void queryClient.invalidateQueries({ queryKey: ['feEmisionConfig'] }); };
  const onOk = (msg: string) => { toast.success(msg); setSolPwd(''); setP12Pwd(''); invalidar(); };
  const onErr = (e: any) => toast.error(e?.response?.data?.message || 'No se pudo guardar');

  const guardarEC = useMutation({
    mutationFn: async () => {
      if (!f) return;
      // El ambiente y el secuencial de oficina se toman del PUNTO elegido (no se editan aquí).
      const op = puntos.find((p) => p.est_codigo === f.establecimiento && p.codigo === f.punto_emision);
      return (await apiClient.post('/rpc/fe_emision_config_set', {
        _ambiente: op?.ambiente || f.ambiente || '1', _establecimiento: f.establecimiento, _punto_emision: f.punto_emision,
        _secuencial_actual: op ? (Number(op.secuencias?.['01']) || 0) : (Number(f.secuencial_actual) || 0),
        _obligado_contabilidad: f.obligado_contabilidad,
        _contribuyente_especial: f.contribuyente_especial || null,
        _rimpe: f.rimpe || null, _agente_retencion: f.agente_retencion || null,
        _dir_matriz: f.dir_matriz || null, _dir_establecimiento: f.dir_establecimiento || null,
        _cert_path: f.cert_path || null, _p12_password: p12Pwd || null,
      }, FIN)).data;
    },
    onSuccess: () => onOk('Configuración guardada'), onError: onErr,
  });

  const guardarPE = useMutation({
    mutationFn: async () => {
      if (!f) return;
      return (await apiClient.post('/rpc/fe_emision_config_pe_set', {
        _ambiente: f.ambiente,
        _serie_factura: f.serie_factura || 'F001', _serie_boleta: f.serie_boleta || 'B001', _serie_nc: f.serie_nc || 'FC01',
        _sol_usuario: f.sol_usuario || null,
        _dir_matriz: f.dir_matriz || null, _dir_establecimiento: f.dir_establecimiento || null,
        _cert_path: f.cert_path || null,
        _sol_password: solPwd || null, _p12_password: p12Pwd || null,
      }, FIN)).data;
    },
    onSuccess: () => onOk('Configuración (PE) guardada'), onError: onErr,
  });

  if (isLoading || !f) {
    return <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const set = (k: keyof Config, v: any) => setF({ ...f, [k]: v });
  const esPE = f.pais === 'PE';

  const tabBtn = (t: typeof tab, label: string) => (
    <button
      className={`rounded-md px-3 py-1.5 text-sm ${tab === t ? 'bg-primary text-primary-foreground font-medium' : 'hover:bg-muted'}`}
      onClick={() => setTab(t)}
    >
      {label}
    </button>
  );

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center gap-3">
        <FileSignature className="w-8 h-8 text-primary" />
        <div>
          <h1 className="text-2xl font-bold">Configuración de Emisión</h1>
          <p className="text-sm text-muted-foreground">
            {esPE
              ? 'Emisión electrónica SUNAT (Perú): Clave SOL, certificado y series de comprobantes.'
              : 'Serie de oficina (facturación especializada y por servicios). El POS numera aparte: cada caja usa su propia serie en POS → Configuración → Cajas.'}
          </p>
        </div>
      </div>

      <div className="flex gap-1 border-b pb-2">
        {tabBtn('emision', 'Emisión')}
        {tabBtn('xml', 'Configuración del XML')}
      </div>

      {tab === 'xml' ? <ConfiguracionXML /> : (
      <div className="max-w-3xl space-y-6">

      {f.configurado === false && (
        <Card className="border-amber-500/40 bg-amber-50/40 dark:bg-amber-900/10">
          <CardContent className="p-4 flex items-start gap-3">
            <ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-sm">Esta empresa todavía no tiene configuración de emisión guardada. Revisa los valores por defecto y presiona <strong>Guardar configuración</strong> para crearla.</p>
          </CardContent>
        </Card>
      )}

      {esPE ? (
        <>
          {/* Estado de credenciales */}
          <Card className={f.cert_configurado && f.p12_configurado && f.sol_configurado
            ? 'border-green-500/40 bg-green-50/40 dark:bg-green-900/10'
            : 'border-amber-500/40 bg-amber-50/40 dark:bg-amber-900/10'}>
            <CardContent className="p-4 flex items-start gap-3">
              {f.cert_configurado && f.p12_configurado && f.sol_configurado
                ? <><ShieldCheck className="w-5 h-5 text-green-600 shrink-0" /><p className="text-sm">Certificado, clave del certificado y Clave SOL configurados. Listo para emitir.</p></>
                : <><ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" /><p className="text-sm">Faltan credenciales para emitir: {[!f.cert_configurado && 'ruta del certificado', !f.p12_configurado && 'clave del certificado', !f.sol_configurado && 'Clave SOL'].filter(Boolean).join(', ')}.</p></>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Ambiente y series</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-amb">Ambiente</label>
                <select id="pe-amb" className={`${selectCls} w-full`} value={f.ambiente} onChange={(e) => set('ambiente', e.target.value)}>
                  <option value="1">Beta / homologación</option>
                  <option value="2">Producción</option>
                </select>
              </div>
              <div />
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-sf">Serie de factura</label>
                <input id="pe-sf" className={inputCls} maxLength={4} placeholder="F001" value={f.serie_factura || ''}
                  onChange={(e) => set('serie_factura', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-sb">Serie de boleta</label>
                <input id="pe-sb" className={inputCls} maxLength={4} placeholder="B001" value={f.serie_boleta || ''}
                  onChange={(e) => set('serie_boleta', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-snc">Serie de nota de crédito</label>
                <input id="pe-snc" className={inputCls} maxLength={4} placeholder="FC01" value={f.serie_nc || ''}
                  onChange={(e) => set('serie_nc', e.target.value.toUpperCase())} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Credenciales SUNAT</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-solu">Usuario SOL (secundario)</label>
                <input id="pe-solu" className={inputCls} placeholder="MODDATOS" value={f.sol_usuario || ''}
                  onChange={(e) => set('sol_usuario', e.target.value)} />
                <p className="text-[11px] text-muted-foreground">Se antepone el RUC para formar el usuario del WS.</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-solp">Clave SOL {f.sol_configurado && <span className="text-green-600">(configurada)</span>}</label>
                <input id="pe-solp" type="password" autoComplete="new-password" className={inputCls}
                  placeholder={f.sol_configurado ? 'En blanco = no cambiar' : 'Requerida'} value={solPwd}
                  onChange={(e) => setSolPwd(e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-cert">Ruta del certificado .p12 (en el servidor)</label>
                <input id="pe-cert" className={inputCls} placeholder="/opt/sri/certs/empresa.p12" value={f.cert_path || ''}
                  onChange={(e) => set('cert_path', e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-p12p">Clave del certificado {f.p12_configurado && <span className="text-green-600">(configurada)</span>}</label>
                <input id="pe-p12p" type="password" autoComplete="new-password" className={inputCls}
                  placeholder={f.p12_configurado ? 'En blanco = no cambiar' : 'Requerida'} value={p12Pwd}
                  onChange={(e) => setP12Pwd(e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Direcciones</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-dm">Domicilio fiscal</label>
                <input id="pe-dm" className={inputCls} value={f.dir_matriz || ''} onChange={(e) => set('dir_matriz', e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="pe-de">Establecimiento anexo (opcional)</label>
                <input id="pe-de" className={inputCls} value={f.dir_establecimiento || ''} onChange={(e) => set('dir_establecimiento', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <div className="flex justify-end">
            <Button isLoading={guardarPE.isPending} onClick={() => {
              const up = (s: string | null) => (s || '').toUpperCase();
              if (!/^F[A-Z0-9]{3}$/.test(up(f.serie_factura))) { toast.error('Serie de factura inválida (F###, p.ej. F001)'); return; }
              if (!/^B[A-Z0-9]{3}$/.test(up(f.serie_boleta))) { toast.error('Serie de boleta inválida (B###, p.ej. B001)'); return; }
              if (!/^[A-Z0-9]{4}$/.test(up(f.serie_nc))) { toast.error('Serie de nota de crédito inválida (4 caracteres, p.ej. FC01)'); return; }
              guardarPE.mutate();
            }}>
              {!guardarPE.isPending && <Save className="w-4 h-4" />} Guardar configuración
            </Button>
          </div>
        </>
      ) : (
        <>
          {/* Estado del certificado */}
          <Card className={f.cert_configurado && f.p12_configurado ? 'border-green-500/40 bg-green-50/40 dark:bg-green-900/10' : 'border-amber-500/40 bg-amber-50/40 dark:bg-amber-900/10'}>
            <CardContent className="p-4 flex items-start gap-3">
              {f.cert_configurado && f.p12_configurado
                ? <><ShieldCheck className="w-5 h-5 text-green-600 shrink-0" /><p className="text-sm">Certificado y clave configurados. Listo para firmar y emitir.</p></>
                : <><ShieldAlert className="w-5 h-5 text-amber-600 shrink-0" /><p className="text-sm">Falta para poder emitir: {[!f.cert_configurado && 'ruta del certificado (.p12)', !f.p12_configurado && 'clave del certificado'].filter(Boolean).join(', ')}. Regístralo abajo.</p></>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><KeyRound className="w-4 h-4" /> Certificado de firma (.p12)</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-cert">Ruta del certificado .p12 (en el servidor)</label>
                <input id="ec-cert" className={inputCls} placeholder="/opt/plataforma/certificados/empresa.p12" value={f.cert_path || ''}
                  onChange={(e) => set('cert_path', e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-p12p">Clave del certificado {f.p12_configurado && <span className="text-green-600">(configurada)</span>}</label>
                <input id="ec-p12p" type="password" autoComplete="new-password" className={inputCls}
                  placeholder={f.p12_configurado ? 'En blanco = no cambiar' : 'Requerida'} value={p12Pwd}
                  onChange={(e) => setP12Pwd(e.target.value)} />
              </div>
              <p className="text-[11px] text-muted-foreground sm:col-span-2">El archivo <strong>.p12</strong> se sube al servidor (por SFTP) a la carpeta de certificados; aquí registras su <strong>ruta</strong> y su <strong>clave</strong> (se guarda cifrada, nunca se muestra). Se aplica al presionar <strong>Guardar configuración</strong>.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Emisión de oficina</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-estab">Establecimiento</label>
                <select id="ec-estab" className={`${selectCls} w-full`} value={f.establecimiento}
                  onChange={(e) => { const v = e.target.value; const ps = puntos.filter((p) => p.est_codigo === v); setF({ ...f, establecimiento: v, punto_emision: ps[0]?.codigo ?? '' }); }}>
                  {f.establecimiento && !establecimientos.some((x) => x.codigo === f.establecimiento) && <option value={f.establecimiento}>{f.establecimiento}</option>}
                  {establecimientos.map((x) => <option key={x.id} value={x.codigo}>{x.codigo}{x.nombre ? ` · ${x.nombre}` : ''}</option>)}
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-pto">Punto de emisión</label>
                <select id="ec-pto" className={`${selectCls} w-full`} value={f.punto_emision} onChange={(e) => set('punto_emision', e.target.value)}>
                  {f.punto_emision && !puntos.some((p) => p.est_codigo === f.establecimiento && p.codigo === f.punto_emision) && <option value={f.punto_emision}>{f.punto_emision}</option>}
                  {puntos.filter((p) => p.est_codigo === f.establecimiento).map((p) => <option key={p.id} value={p.codigo}>{p.codigo}{p.nombre ? ` · ${p.nombre}` : ''} · {p.ambiente === '2' ? 'Producción' : 'Pruebas'}</option>)}
                </select>
              </div>
              <p className="text-[11px] text-muted-foreground sm:col-span-2">Serie por defecto de las <strong>facturas de oficina</strong> (por servicios y especializada). El ambiente y los secuenciales salen del punto elegido — se administran abajo. El POS numera con la serie de cada caja.</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Régimen del emisor</CardTitle></CardHeader>
            <CardContent className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-1 flex items-center gap-2 pt-6">
                <input id="ec-cont" type="checkbox" className="h-4 w-4 rounded border-input"
                  checked={f.obligado_contabilidad} onChange={(e) => set('obligado_contabilidad', e.target.checked)} />
                <label htmlFor="ec-cont" className="text-sm">Obligado a llevar contabilidad</label>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-rimpe">RIMPE</label>
                <select id="ec-rimpe" className={`${selectCls} w-full`} value={f.rimpe || ''} onChange={(e) => set('rimpe', e.target.value)}>
                  <option value="">Ninguno</option>
                  <option value="CONTRIBUYENTE RÉGIMEN RIMPE">Emprendedor</option>
                  <option value="CONTRIBUYENTE NEGOCIO POPULAR - RÉGIMEN RIMPE">Negocio Popular</option>
                </select>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-esp">Contribuyente especial (nº resolución)</label>
                <input id="ec-esp" className={inputCls} placeholder="Opcional" value={f.contribuyente_especial || ''}
                  onChange={(e) => set('contribuyente_especial', e.target.value)} />
              </div>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-ag">Agente de retención (nº resolución)</label>
                <input id="ec-ag" className={inputCls} placeholder="Opcional" value={f.agente_retencion || ''}
                  onChange={(e) => set('agente_retencion', e.target.value)} />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-base">Dirección matriz</CardTitle></CardHeader>
            <CardContent>
              <div className="space-y-1">
                <label className="text-xs font-medium" htmlFor="ec-dm">Dirección matriz (domicilio fiscal)</label>
                <input id="ec-dm" className={inputCls} value={f.dir_matriz || ''} onChange={(e) => set('dir_matriz', e.target.value)} />
                <p className="text-[11px] text-muted-foreground">Va en <code>dirMatriz</code> del comprobante. La dirección de cada local se define en <strong>Establecimientos</strong> (abajo).</p>
              </div>
            </CardContent>
          </Card>

          <EstablecimientosPuntos />

          <div className="flex justify-end">
            <Button isLoading={guardarEC.isPending} onClick={() => {
              if (!/^\d{3}$/.test(f.establecimiento) || !/^\d{3}$/.test(f.punto_emision)) {
                toast.error('Establecimiento y punto de emisión deben ser 3 dígitos'); return;
              }
              guardarEC.mutate();
            }}>
              {!guardarEC.isPending && <Save className="w-4 h-4" />} Guardar configuración
            </Button>
          </div>

          <EmisionAutomatica />
        </>
      )}
      </div>
      )}
    </div>
  );
}
